import { IconoItem, labelFamilia } from '@/lib/cd2bIconos'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'

/**
 * SelectorModulos — la lista de módulos y complementos que el asesor marca para armar el paquete.
 *
 * Vivía DUPLICADA en los dos sitios donde se arma un paquete: el editor de cotización nueva
 * (`ConsultorCotizaciones/Editor`) y el "Cambiar paquete" de la ficha del cliente
 * (`Clientes/Ficha`). Misma semántica en los dos —incluido en el plan vs. add-on que suma, y las
 * tres familias de complemento— pero maquetada distinta, así que se veían como dos pantallas de
 * productos diferentes y cualquier arreglo había que hacerlo dos veces (la última: mostrar POR QUÉ
 * un módulo entró solo por dependencia).
 *
 * `variant` es lo único que cambia, y es una decisión de espacio, no de significado:
 *   · `tarjetas` — dos columnas con descripción. Para el editor, que tiene la página entera.
 *   · `lista`    — una línea por módulo. Para el panel de la ficha, que comparte ancho con el
 *                  cuadro de impacto.
 *
 * @param {object[]} modulos      catálogo (`catalogo.modulos`), con `precio`, `optIn`, `incluidoEn`, `requiere`, `moduloFisico`
 * @param {object[]} addons       complementos recurrentes ya aplanados (soporte + acceso + negocio)
 * @param {string}   edicion      edición elegida — decide qué módulos van "Incluidos"
 * @param {string[]} seleccion    códigos marcados por el asesor
 * @param {object[]} dependencias `[{code, label, requeridoPorLabel}]` que el motor añadió solo
 * @param {(code:string)=>void} onToggleModulo
 * @param {(addon:object)=>boolean} addonActivo
 * @param {(addon:object)=>void} onToggleAddon
 * @param {(n:number)=>string} dinero
 * @param {boolean} extrasBloqueados edición sin tarjeta registrada (Emprendedor): los add-ons
 *   recurrentes se muestran deshabilitados — no hay forma de cobrarle un cargo a este cliente.
 */
export default function SelectorModulos({
  modulos = [],
  addons = [],
  edicion,
  seleccion = [],
  dependencias = [],
  onToggleModulo,
  addonActivo,
  onToggleAddon,
  dinero,
  variant = 'tarjetas',
  extrasBloqueados = false,
}) {
  const tarjetas = variant === 'tarjetas'
  // Módulos que el asesor NO marcó y entraron porque otro los exige. Se señalan aparte porque
  // desmarcarlos no sirve de nada —el motor los vuelve a añadir— y sin decirlo parece un fallo.
  const porDependencia = new Map(dependencias.map((d) => [d.code, d]))
  const nombreDe = (code) => modulos.find((m) => m.code === code)?.label || code
  // Varios SKU comparten el mismo módulo DESCARGABLE (Cobranza↔Crédito, Multiempresa↔Contaduría
  // — ver `moduloFisico` en cd2bCatalogo.js): sin avisarlo, el asesor los lee como 2 descargas
  // distintas cuando el cliente en realidad recibe una sola.
  const compartidoCon = (m) => (m.moduloFisico && m.moduloFisico !== m.code)
    ? (modulos.find((x) => x.code === m.moduloFisico)?.label || m.moduloFisico)
    : null
  // Reconciliación con "cuántos módulos bajan en la app": el asesor cuenta SKU marcados (el
  // negocio), no descargas (el instalador). Sin este total, Cobranza+Crédito se leen como 2
  // módulos que instalan aparte cuando en la app son 1 solo paquete.
  const seleccionados = modulos.filter((m) => seleccion.includes(m.code))
  const gruposReales = new Set(seleccionados.map((m) => m.moduloFisico || m.code)).size

  const marco = (activo, extra) => tarjetas
    ? `flex items-start gap-2 rounded-lg border p-3 cursor-pointer ${
      activo ? (extra ? 'border-warning-300 dark:border-warning-700 bg-warning-50/50 dark:bg-warning/10' : 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/40') : 'border-neutral-200 dark:border-neutral-800'
    }`
    : 'flex items-center justify-between gap-2 text-body-sm text-neutral-700 dark:text-neutral-300'

  return (
    <div className="space-y-2">
      {seleccionados.length > 0 && (
        <p className="text-caption text-neutral-500 dark:text-neutral-400">
          {seleccionados.length} módulo{seleccionados.length === 1 ? '' : 's'} vendido{seleccionados.length === 1 ? '' : 's'}
          {seleccionados.length !== gruposReales && ` · ${gruposReales} instalable${gruposReales === 1 ? '' : 's'} en la app`}
        </p>
      )}

      <div className={tarjetas ? 'grid gap-2 sm:grid-cols-2' : 'space-y-1.5'}>
      {modulos.map((m) => {
        const incluido = (m.incluidoEn || []).includes(edicion)
        // Lo que la edición incluye por su precio llega SIEMPRE (ya no hay recorte por paquete):
        // se muestra marcado y no se puede desmarcar, porque «Excluido» no significaría nada.
        const marcado = incluido || seleccion.includes(m.code)
        const alternar = () => { if (!incluido) onToggleModulo(m.code) }
        const dep = porDependencia.get(m.code)
        const compartido = compartidoCon(m)
        // Requisitos que aún NO están marcados: se avisan ANTES de marcar, para que el asesor
        // sepa que ese módulo arrastra otro cobro.
        const faltantes = (m.requiere || []).filter((c) => !seleccion.includes(c))

        const etiqueta = incluido
          ? <Badge variant="success">Incluido</Badge>
          : (
            <Badge variant={marcado ? 'warning' : 'outline'}>
              {m.preventa ? 'Preventa' : m.optIn ? 'Vertical' : 'Add-on'} +{dinero(m.precio)}
            </Badge>
          )

        return (
          <label key={m.code} className={marco(marcado, marcado && !incluido)}>
            <span className={tarjetas ? 'contents' : 'flex items-center gap-2 min-w-0'}>
              {tarjetas
                ? <Checkbox checked={marcado} disabled={incluido} onCheckedChange={alternar} />
                : <input type="checkbox" className="h-4 w-4 shrink-0 accent-primary" checked={marcado} disabled={incluido} onChange={alternar} />}
              <IconoItem item={m} size={16} className={tarjetas ? 'mt-0.5 shrink-0' : 'shrink-0'} />
              {!tarjetas && (
                <span className="min-w-0">
                  <span className="block truncate">{m.label}</span>
                  {/* Preventa: el asesor tiene que saber que esto NO se instala todavía, o le
                      promete al cliente una pantalla que no va a aparecer. */}
                  {m.preventa && (
                    <span className="block truncate text-caption text-warning-700 dark:text-warning-300">Preventa · aún no se entrega</span>
                  )}
                  {/* Preventa PARCIAL (doc 176 A.1): el módulo se instala, pero esta parte aún no. */}
                  {(m.capacidadesPreventa || []).map((c) => (
                    <span key={c.id} className="block truncate text-caption text-warning-700 dark:text-warning-300">{c.texto} · Preventa · llega pronto</span>
                  ))}
                  {compartido && (
                    <span className="block truncate text-caption text-neutral-400 dark:text-neutral-500">Mismo paquete que {compartido}</span>
                  )}
                </span>
              )}
            </span>

            {tarjetas ? (
              <span className="text-body-sm min-w-0">
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="font-medium text-neutral-900 dark:text-neutral-50">{m.label}</span>
                  {etiqueta}
                  {dep && <Badge variant="warning">Lo exige {dep.requeridoPorLabel}</Badge>}
                </span>
                <span className="block text-caption text-neutral-500 dark:text-neutral-400 mt-0.5">{m.desc}</span>
                {m.preventa && (
                  <span className="block text-caption text-warning-700 dark:text-warning-300 mt-0.5">
                    Preventa · se cobra desde ya, pero el módulo aún no se instala. Dígaselo al cliente.
                  </span>
                )}
                {(m.capacidadesPreventa || []).map((c) => (
                  <span key={c.id} className="block text-caption text-warning-700 dark:text-warning-300 mt-0.5">
                    {c.texto} · Preventa · llega pronto. El módulo se instala y funciona; esta parte todavía no.
                  </span>
                ))}
                {compartido && (
                  <span className="block text-caption text-neutral-400 dark:text-neutral-500 mt-0.5">
                    Mismo paquete descargable que {compartido} — se cobran aparte, se instalan juntos.
                  </span>
                )}
                {!marcado && faltantes.length > 0 && (
                  <span className="block text-caption text-warning-700 dark:text-warning-300 mt-1">
                    Necesita {faltantes.map(nombreDe).join(', ')} · se añade solo y suma su precio
                  </span>
                )}
              </span>
            ) : (
              <span className="flex items-center gap-1.5 shrink-0">
                {dep && <Badge variant="warning">Lo exige {dep.requeridoPorLabel}</Badge>}
                {etiqueta}
              </span>
            )}
          </label>
        )
      })}
      </div>

      {/* Complementos recurrentes: soporte, canal de acceso (app móvil) y negocio adicional. No
          son áreas ni tienen dependencias — se marcan o no. Emprendedor no tiene tarjeta
          registrada (alta gratis, sin Stripe): quedan deshabilitados en esa edición.
          `incluidoEn`: algunos (hoy solo app móvil, en Emprendedor/Micro) ya vienen gratis con
          la edición — ni se cobran ni se pueden marcar/desmarcar, así que se pintan aparte del
          bloqueo genérico (que antes los mostraba como "no disponibles" en vez de "ya la tienes").
          Separados de los módulos: no son paquetes descargables, no cuentan en `gruposReales`. */}
      {addons.length > 0 && (
        <p className="text-micro text-neutral-400 dark:text-neutral-500 uppercase pt-1">Canales de acceso y soporte</p>
      )}
      <div className={tarjetas ? 'grid gap-2 sm:grid-cols-2' : 'space-y-1.5'}>
      {addons.map((a) => {
        const incluidoPorEdicion = (a.incluidoEn || []).includes(edicion)
        const on = incluidoPorEdicion || addonActivo(a)
        const bloqueado = !incluidoPorEdicion && extrasBloqueados && (a.precio || 0) > 0
        const etiqueta = incluidoPorEdicion
          ? <Badge variant="success">Incluido</Badge>
          : <Badge variant={on ? 'warning' : 'outline'}>{labelFamilia(a.tipo)} +{dinero(a.precio)}/mes</Badge>
        return (
          <label key={a.code} className={`${marco(on, on && !incluidoPorEdicion)} ${bloqueado ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <span className={tarjetas ? 'contents' : 'flex items-center gap-2 min-w-0'}>
              {tarjetas
                ? <Checkbox checked={on} disabled={bloqueado || incluidoPorEdicion} onCheckedChange={() => !bloqueado && !incluidoPorEdicion && onToggleAddon(a)} />
                : <input type="checkbox" className="h-4 w-4 shrink-0 accent-primary" checked={on} disabled={bloqueado || incluidoPorEdicion} onChange={() => !bloqueado && !incluidoPorEdicion && onToggleAddon(a)} />}
              <IconoItem item={a} size={16} className={tarjetas ? 'mt-0.5 shrink-0' : 'shrink-0'} />
              {!tarjetas && <span className="truncate">{a.label}</span>}
            </span>
            {tarjetas ? (
              <span className="text-body-sm min-w-0">
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="font-medium text-neutral-900 dark:text-neutral-50">{a.label}</span>
                  {etiqueta}
                </span>
                <span className="block text-caption text-neutral-500 dark:text-neutral-400 mt-0.5">{a.desc}</span>
                {incluidoPorEdicion && (
                  <span className="block text-caption text-success-700 dark:text-success-300 mt-1">
                    Ya incluida en esta edición — no se cobra aparte.
                  </span>
                )}
                {bloqueado && (
                  <span className="block text-caption text-warning-700 dark:text-warning-300 mt-1">
                    Requiere edición Micro o superior — Emprendedor no tiene tarjeta registrada
                  </span>
                )}
              </span>
            ) : (
              etiqueta
            )}
          </label>
        )
      })}
      </div>
    </div>
  )
}
